import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import StarRating from "../components/StarRating";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { CheckCircle2, Camera, Video, Sparkles, ShieldCheck, Heart } from "lucide-react";
import { toast } from "sonner";
import Seo from "../lib/Seo";

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || "";

export default function Feedback() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "", city: "", email: "", phone: "",
    service: "", rating: 0, review: "", consent: false, website: "",
  });
  const [photoId, setPhotoId] = useState(null);
  const [photoName, setPhotoName] = useState("");
  const [videoId, setVideoId] = useState(null);
  const [videoName, setVideoName] = useState("");
  const [uploading, setUploading] = useState(null); // "photo" | "video" | null
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  useEffect(() => {
    api.get("/reviews/services").then((r) => setServices(r.data.services || []));
  }, []);

  const uploadFile = async (file, kind) => {
    if (!file) return;
    const maxMb = kind === "video" ? 20 : 3;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`${kind === "video" ? "Video" : "Photo"} must be under ${maxMb} MB.`);
      return;
    }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/reviews/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "photo") { setPhotoId(data.id); setPhotoName(file.name); }
      else { setVideoId(data.id); setVideoName(file.name); }
      toast.success(`${kind === "video" ? "Video" : "Photo"} attached`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please share your name.");
    if (!form.service) return toast.error("Please choose the service you availed.");
    if (!form.rating) return toast.error("Please give a star rating.");
    if (form.review.trim().length < 15) return toast.error("Please share a slightly more detailed review (15+ chars).");
    if (!form.consent) return toast.error("Please tick the consent box so we can publish your feedback.");
    if (HCAPTCHA_SITE_KEY && !captchaToken) return toast.error("Please complete the captcha.");

    setBusy(true);
    try {
      await api.post("/reviews", {
        ...form,
        rating: Number(form.rating),
        photo_id: photoId,
        video_id: videoId,
        hcaptcha_token: captchaToken,
      });
      navigate("/feedback/thank-you");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your review.");
      if (captchaRef.current) captchaRef.current.resetCaptcha();
      setCaptchaToken("");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="feedback-page" className="min-h-screen">
      <Seo
        title="Share Your Experience — Feedback for YourAstroHub"
        description="Share your experience with YourAstroHub. Your feedback helps us serve seekers around the world with more clarity and warmth."
        path="/feedback"
      />

      {/* HERO */}
      <section className="relative overflow-hidden -mt-20 pt-32 md:pt-40 pb-16 md:pb-24 text-[#F5E8D8] bg-[#0B1F3A]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-40 w-[520px] h-[520px] rounded-full bg-[#0E9F6E]/12 blur-[120px]" />
          <div className="absolute -bottom-32 right-0 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <div className="eyebrow flex items-center justify-center gap-3 mb-6">
            <span className="w-8 gold-hairline" /> Your Voice Matters <span className="w-8 gold-hairline" />
          </div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05]">
            Share your <span className="gold-text italic">experience</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#F5E8D8]/75 max-w-2xl mx-auto leading-relaxed">
            Your honest words help fellow seekers find guidance. Whether you attended a consultation,
            a course, a webinar or purchased a Rudraksha — we would love to hear from you.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-[#F5E8D8]/70">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Reviewed before publishing</span>
            <span className="inline-flex items-center gap-2"><Heart className="w-4 h-4 text-[#D4AF37]" /> Every review is read personally</span>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <form onSubmit={submit} className="rounded-3xl border border-foreground/10 bg-background p-6 md:p-10 space-y-6">
            {/* honeypot — hidden field bots love to fill */}
            <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
              tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fb-name">Full Name *</Label>
                <Input id="fb-name" data-testid="fb-name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-full mt-2" placeholder="Your name" />
              </div>
              <div>
                <Label htmlFor="fb-city">City *</Label>
                <Input id="fb-city" data-testid="fb-city" required value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="rounded-full mt-2" placeholder="City, Country" />
              </div>
              <div>
                <Label htmlFor="fb-email">Email <span className="text-foreground/40">(optional)</span></Label>
                <Input id="fb-email" data-testid="fb-email" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-full mt-2" placeholder="you@example.com" />
              </div>
              <div>
                <Label htmlFor="fb-phone">Mobile <span className="text-foreground/40">(optional)</span></Label>
                <Input id="fb-phone" data-testid="fb-phone" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-full mt-2" placeholder="+91 ..." />
              </div>
            </div>

            <div>
              <Label>Service you availed *</Label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="fb-service" className="rounded-full mt-2">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-2xl border border-foreground/10 p-5 bg-[#F4EFE6]/40 dark:bg-[#0F284B]/60">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <Label>Your rating *</Label>
                  <div className="text-xs text-foreground/60 mt-1">Tap the stars to rate your experience.</div>
                </div>
                <StarRating value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size="lg" data-testid="fb-rating" />
              </div>
            </div>

            <div>
              <Label htmlFor="fb-review">Your review *</Label>
              <Textarea id="fb-review" data-testid="fb-review" rows={6} required
                value={form.review} onChange={(e) => setForm({ ...form, review: e.target.value })}
                className="rounded-2xl mt-2"
                placeholder="Share what you loved, what changed, or what you'd tell a friend considering YourAstroHub." />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <label className="flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-foreground/20 hover:border-[#D4AF37] cursor-pointer transition-colors">
                <div className="w-10 h-10 rounded-full grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                  <Camera className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div className="text-sm flex-1">
                  <div className="font-medium">{photoName || (uploading === "photo" ? "Uploading photo…" : "Attach a photo")}</div>
                  <div className="text-xs text-foreground/60">JPG / PNG · Max 3 MB · optional</div>
                </div>
                <input type="file" accept="image/*" data-testid="fb-photo" className="hidden"
                  onChange={(e) => uploadFile(e.target.files?.[0], "photo")} />
              </label>

              <label className="flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-foreground/20 hover:border-[#D4AF37] cursor-pointer transition-colors">
                <div className="w-10 h-10 rounded-full grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                  <Video className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div className="text-sm flex-1">
                  <div className="font-medium">{videoName || (uploading === "video" ? "Uploading video…" : "Video testimonial")}</div>
                  <div className="text-xs text-foreground/60">MP4 / MOV · Max 20 MB · optional</div>
                </div>
                <input type="file" accept="video/*" data-testid="fb-video" className="hidden"
                  onChange={(e) => uploadFile(e.target.files?.[0], "video")} />
              </label>
            </div>

            <div className="flex items-start gap-3 rounded-2xl border border-foreground/10 p-4">
              <Checkbox id="fb-consent" data-testid="fb-consent" checked={form.consent}
                onCheckedChange={(v) => setForm({ ...form, consent: !!v })} className="mt-1" />
              <Label htmlFor="fb-consent" className="text-sm leading-snug cursor-pointer">
                I authorize YourAstroHub to publish my feedback (including my name, city, photo and video, if provided) on
                its website and social media channels.
              </Label>
            </div>

            {HCAPTCHA_SITE_KEY ? (
              <div data-testid="fb-hcaptcha">
                <HCaptcha
                  ref={captchaRef}
                  sitekey={HCAPTCHA_SITE_KEY}
                  onVerify={(t) => setCaptchaToken(t)}
                  onExpire={() => setCaptchaToken("")}
                />
              </div>
            ) : null}

            <Button type="submit" size="lg" disabled={busy || uploading}
              data-testid="fb-submit"
              className="w-full rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold py-6 disabled:opacity-50">
              {busy ? "Submitting…" : (<><Sparkles className="w-4 h-4 mr-2" />Submit your review</>)}
            </Button>

            <p className="text-center text-xs text-foreground/50">
              Reviews are moderated for authenticity before appearing on the site.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

export function FeedbackThankYou() {
  return (
    <div data-testid="feedback-thank-you" className="min-h-[70vh] grid place-items-center px-6 py-24">
      <div className="max-w-lg w-full rounded-3xl border border-[#D4AF37]/40 p-10 text-center bg-background">
        <div className="w-14 h-14 rounded-full grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 mx-auto mb-5">
          <CheckCircle2 className="w-7 h-7 text-[#D4AF37]" />
        </div>
        <h2 className="font-serif text-3xl mb-2">Thank you 🙏</h2>
        <p className="text-foreground/70 mb-6">
          Your feedback has been received and will be reviewed shortly. We deeply appreciate the time you took
          to share your experience with YourAstroHub.
        </p>
        <a href="/">
          <Button className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A]">Back to home</Button>
        </a>
      </div>
    </div>
  );
}
